import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
const UPLOADS_BASE = API_BASE.startsWith("/") ? "" : API_BASE;

const DOT_GRID =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36'%3E%3Ccircle cx='18' cy='18' r='1' fill='rgba(90,144,168,0.10)'/%3E%3C/svg%3E\")";

const TIMEZONES = [
  "Asia/Kolkata","Asia/Dubai","Asia/Singapore","Asia/Tokyo","Asia/Shanghai",
  "Asia/Bangkok","Asia/Karachi","Asia/Dhaka","Europe/London","Europe/Paris",
  "Europe/Berlin","Europe/Moscow","America/New_York","America/Chicago",
  "America/Denver","America/Los_Angeles","America/Sao_Paulo",
  "Africa/Cairo","Africa/Johannesburg","Australia/Sydney","Pacific/Auckland",
];

const GENDER_OPTIONS = [
  { label: "Male",              value: "male" },
  { label: "Female",            value: "female" },
  { label: "Non-binary",        value: "non_binary" },
  { label: "Prefer not to say", value: "prefer_not_to_say" },
];

const WORK_TYPES = [
  { label: "Desk Work",        value: "desk_work" },
  { label: "Field Work",       value: "field_work" },
  { label: "Physical Labour",  value: "physical_labour" },
  { label: "Mixed",            value: "mixed" },
  { label: "Retired",          value: "retired" },
  { label: "Student",          value: "student" },
];

const DIET_TYPES = [
  { label: "Omnivore",     value: "omnivore" },
  { label: "Vegetarian",   value: "vegetarian" },
  { label: "Vegan",        value: "vegan" },
  { label: "Pescatarian",  value: "pescatarian" },
  { label: "Keto",         value: "keto" },
  { label: "Paleo",        value: "paleo" },
  { label: "Other",        value: "other" },
];

const SLEEP_QUALITY = [
  { label: "Poor",      value: "poor" },
  { label: "Fair",      value: "fair" },
  { label: "Good",      value: "good" },
  { label: "Excellent", value: "excellent" },
];

const EXERCISE_FREQUENCY = [
  { label: "None",           value: "none" },
  { label: "1× / week",      value: "1x_week" },
  { label: "2× / week",      value: "2x_week" },
  { label: "3× / week",      value: "3x_week" },
  { label: "4× / week",      value: "4x_week" },
  { label: "Daily",          value: "daily" },
];

const STRESS_LEVELS = [
  { label: "Low",       value: "low" },
  { label: "Moderate",  value: "moderate" },
  { label: "High",      value: "high" },
  { label: "Very High", value: "very_high" },
];

const ALCOHOL_OPTIONS = [
  { label: "None",       value: "none" },
  { label: "Occasional", value: "occasional" },
  { label: "Moderate",   value: "moderate" },
  { label: "Heavy",      value: "heavy" },
];

const SMOKING_OPTIONS = [
  { label: "Never",     value: "never" },
  { label: "Ex-Smoker", value: "ex_smoker" },
  { label: "Current",   value: "current" },
];

const HORMONAL_STATUS = [
  { label: "Regular Cycles",  value: "regular_cycles" },
  { label: "Irregular",       value: "irregular" },
  { label: "Perimenopause",   value: "perimenopause" },
  { label: "Menopause",       value: "menopause" },
  { label: "Post-Menopause",  value: "post_menopause" },
  { label: "On HRT",          value: "on_hrt" },
  { label: "Not Applicable",  value: "not_applicable" },
];

const EXERCISE_SUGGESTIONS = [
  "Walking", "Running", "Cycling", "Swimming", "Yoga", "Strength Training",
  "HIIT", "Pilates", "Martial Arts", "Tennis", "Football", "Basketball",
];

const CONDITION_SUGGESTIONS = [
  "Diabetes", "Hypertension", "Asthma", "Hypothyroidism", "PCOS",
  "Anxiety", "Depression", "Heart Disease", "Arthritis", "Obesity",
  "High Cholesterol", "Sleep Apnea", "IBS",
];

/* ── Shared styles ── */
const fieldBase = (focused) => ({
  display: "block", width: "100%", padding: "12px 14px",
  border: `1px solid ${focused ? "#5A90A8" : "rgba(90,144,168,0.25)"}`,
  borderRadius: 10, fontSize: 14, fontFamily: "Inter, sans-serif",
  color: "#1C3545", background: "#F5FAFC", outline: "none",
  boxSizing: "border-box", transition: "border-color 180ms ease, box-shadow 180ms ease",
  boxShadow: focused ? "0 0 0 3px rgba(90,144,168,0.12)" : "none",
});
const textareaBase = (focused) => ({
  ...fieldBase(focused), resize: "vertical", minHeight: 88, lineHeight: 1.6,
});
const labelStyle = {
  fontSize: 13, fontWeight: 500, color: "#4D7A8E",
  fontFamily: "Inter, sans-serif", display: "block", marginBottom: 7,
};
const sectionLabel = {
  fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
  color: "#5A90A8", fontFamily: "Inter, sans-serif", marginBottom: 14,
};
const optionalTag = { marginLeft: 8, fontSize: 11, color: "#A8C8D8", fontWeight: 400 };
const divider = { borderTop: "1px solid rgba(90,144,168,0.12)", paddingTop: 24, marginBottom: 18 };

/* ── Pill ── */
function Pill({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{ padding: "8px 14px", borderRadius: 999, border: `1px solid ${active ? "#5A90A8" : "rgba(90,144,168,0.25)"}`, background: active ? "rgba(90,144,168,0.12)" : "transparent", color: active ? "#5A90A8" : "#7AAABB", fontSize: 13, fontWeight: active ? 600 : 400, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 180ms ease", whiteSpace: "nowrap" }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.borderColor = "#5A90A8"; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.borderColor = "rgba(90,144,168,0.25)"; }}
    >
      {label}
    </button>
  );
}

/* ── Tag input ── */
function TagInput({ tags, onChange, placeholder, suggestions = [], disabled }) {
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);

  const add = (val) => {
    const v = val.trim();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setInput("");
  };
  const remove = (tag) => onChange(tags.filter((t) => t !== tag));
  const onKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); add(input); }
    if (e.key === "Backspace" && !input && tags.length > 0) onChange(tags.slice(0, -1));
  };

  return (
    <div>
      {tags.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          {tags.map((tag) => (
            <span key={tag} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px 5px 12px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.3)", color: "#5A90A8", fontSize: 13, fontFamily: "Inter, sans-serif" }}>
              {tag}
              <button type="button" onClick={() => remove(tag)} style={{ background: "none", border: "none", cursor: "pointer", color: "#7AAABB", padding: 0, lineHeight: 1 }}>✕</button>
            </span>
          ))}
        </div>
      )}
      <input
        type="text" value={input} placeholder={placeholder} disabled={disabled}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => { add(input); setFocused(false); }}
        onFocus={() => setFocused(true)}
        style={fieldBase(focused)}
      />
      {suggestions.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {suggestions.filter((s) => !tags.includes(s)).slice(0, 8).map((s) => (
            <button key={s} type="button" onClick={() => add(s)}
              style={{ padding: "4px 10px", borderRadius: 999, border: "1px solid rgba(90,144,168,0.2)", background: "transparent", color: "#7AAABB", fontSize: 12, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#5A90A8"; e.currentTarget.style.color = "#5A90A8"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.2)"; e.currentTarget.style.color = "#7AAABB"; }}
            >+ {s}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Stepper ── */
function Stepper({ current }) {
  const steps = ["Profile"]; // Lifestyle, Health History, Complete hidden — re-enable when needed
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0, marginBottom: 36 }}>
      {steps.map((label, i) => {
        const idx = i + 1;
        const done = idx < current;
        const active = idx === current;
        return (
          <div key={label} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${done ? "#5A90A8" : active ? "#5A90A8" : "rgba(90,144,168,0.25)"}`, background: done ? "#5A90A8" : active ? "rgba(90,144,168,0.12)" : "transparent", transition: "all 300ms ease" }}>
                {done
                  ? <svg width="14" height="14" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  : <span style={{ fontSize: 13, fontWeight: 700, color: active ? "#5A90A8" : "#9BBCCC", fontFamily: "Inter, sans-serif" }}>{idx}</span>
                }
              </div>
              <span style={{ fontSize: 11, fontWeight: active ? 600 : 400, color: active ? "#5A90A8" : done ? "#8AAEBB" : "#9BBCCC", fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}>{label}</span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ width: 56, height: 2, background: done ? "rgba(90,144,168,0.5)" : "rgba(90,144,168,0.12)", margin: "0 8px", marginBottom: 22, transition: "background 300ms ease" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Submit button ── */
function SubmitBtn({ canSubmit, loading, label }) {
  return (
    <button type="submit" disabled={!canSubmit}
      style={{ display: "block", width: "100%", padding: "14px", background: canSubmit ? "#5A90A8" : "rgba(90,144,168,0.25)", color: canSubmit ? "#FFFFFF" : "rgba(90,144,168,0.5)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: canSubmit ? "pointer" : "not-allowed", letterSpacing: "0.01em", transition: "background 180ms ease, transform 180ms ease, box-shadow 180ms ease" }}
      onMouseEnter={(e) => { if (canSubmit) { e.currentTarget.style.background = "#4D7A8E"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(90,144,168,0.3)"; } }}
      onMouseLeave={(e) => { if (canSubmit) { e.currentTarget.style.background = "#5A90A8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}
    >
      {loading ? "Saving…" : label}
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════
   Main Component
   ════════════════════════════════════════════════════════════════ */
export default function PatientOnboardingPage({ patientName = "", onFinish, onLogout }) {
  const { logout } = useAuth();
  const [step, setStep] = useState(null); // null=loading, 1|2|3|4|"success"

  /* ── Step 1 state — Basic Profile ── */
  const [displayName, setDisplayName] = useState(patientName);
  const [profilePhoto, setProfilePhoto] = useState("");
  const [profilePhotoPreview, setProfilePhotoPreview] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const pendingPhotoRef = useRef(null);
  const [phone, setPhone]         = useState("");
  const [gender, setGender]       = useState("");
  const [dob, setDob]             = useState("");
  const [city, setCity]           = useState("");
  const [country, setCountry]     = useState("");
  const [timezone, setTimezone]   = useState("Asia/Kolkata");
  const [isProfileSaved, setIsProfileSaved] = useState(false);

  /* ── Step 2 state — Lifestyle ── */
  const [occupation, setOccupation]           = useState("");
  const [workType, setWorkType]               = useState("");
  const [dietType, setDietType]               = useState("");
  const [dietNotes, setDietNotes]             = useState("");
  const [sleepHours, setSleepHours]           = useState("");
  const [sleepQuality, setSleepQuality]       = useState("");
  const [exerciseFreq, setExerciseFreq]       = useState("");
  const [exerciseTypes, setExerciseTypes]     = useState([]);
  const [stressLevel, setStressLevel]         = useState("");
  const [alcohol, setAlcohol]                 = useState("");
  const [smoking, setSmoking]                 = useState("");
  const [waterIntake, setWaterIntake]         = useState("");

  /* ── Step 3 state — Health History ── */
  const [conditions, setConditions]           = useState([]);
  const [surgeries, setSurgeries]             = useState("");
  const [familyHistory, setFamilyHistory]     = useState([]);
  const [medications, setMedications]         = useState("");
  const [allergies, setAllergies]             = useState("");
  const [hormonalStatus, setHormonalStatus]   = useState("");

  /* ── focus state ── */
  const [focused, setFocused] = useState({});
  const f = (key) => ({ onFocus: () => setFocused((p) => ({ ...p, [key]: true })), onBlur: () => setFocused((p) => ({ ...p, [key]: false })) });

  /* ── async ── */
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  const handleError = (err) => {
    console.error(err);
    if (!err.response) { setError(`Cannot connect to server. Make sure the backend is running on ${API_BASE}`); return; }
    const detail = err.response?.data?.detail;
    if (typeof detail === "string") setError(detail);
    else if (Array.isArray(detail)) setError(detail.map((d) => d.msg).join(", "));
    else setError(`Server error ${err.response.status}: ${err.response.statusText || "Request failed."}`);
  };

  // On mount: check onboarding status and pre-fill fields
  useEffect(() => {
    const init = async () => {
      try {
        const { data: status } = await api.get("/patient/onboarding/status");

        if (status.profile_complete) {
          // Pre-fill profile fields
          try {
            const { data: profile } = await api.get("/patient/onboarding/me");
            if (profile.display_name)      setDisplayName(profile.display_name);
            if (profile.profile_photo_url) {
              setProfilePhoto(profile.profile_photo_url);
              setProfilePhotoPreview(UPLOADS_BASE + profile.profile_photo_url);
            }
            if (profile.phone)             setPhone(profile.phone);
            if (profile.gender)            setGender(profile.gender);
            if (profile.date_of_birth)     setDob(profile.date_of_birth);
            if (profile.city)              setCity(profile.city);
            if (profile.country)           setCountry(profile.country);
            if (profile.timezone)          setTimezone(profile.timezone);
            setIsProfileSaved(true);
          } catch { /* non-fatal */ }
        }

        if (status.lifestyle_complete) {
          try {
            const { data: lifestyle } = await api.get("/patient/onboarding/lifestyle");
            if (lifestyle.occupation)          setOccupation(lifestyle.occupation || "");
            if (lifestyle.work_type)           setWorkType(lifestyle.work_type || "");
            if (lifestyle.diet_type)           setDietType(lifestyle.diet_type || "");
            if (lifestyle.diet_notes)          setDietNotes(lifestyle.diet_notes || "");
            if (lifestyle.sleep_hours_avg != null) setSleepHours(String(lifestyle.sleep_hours_avg));
            if (lifestyle.sleep_quality)       setSleepQuality(lifestyle.sleep_quality || "");
            if (lifestyle.exercise_frequency)  setExerciseFreq(lifestyle.exercise_frequency || "");
            if (lifestyle.exercise_types?.length) setExerciseTypes(lifestyle.exercise_types);
            if (lifestyle.stress_level)        setStressLevel(lifestyle.stress_level || "");
            if (lifestyle.alcohol_consumption) setAlcohol(lifestyle.alcohol_consumption || "");
            if (lifestyle.smoking_status)      setSmoking(lifestyle.smoking_status || "");
            if (lifestyle.water_intake_litres != null) setWaterIntake(String(lifestyle.water_intake_litres));
          } catch { /* non-fatal */ }
        }

        if (status.health_history_complete) {
          try {
            const { data: hh } = await api.get("/patient/onboarding/health-history");
            if (hh.existing_conditions?.length) setConditions(hh.existing_conditions);
            if (hh.past_surgeries)              setSurgeries(hh.past_surgeries || "");
            if (hh.family_history?.length)      setFamilyHistory(hh.family_history);
            if (hh.current_medications)         setMedications(hh.current_medications || "");
            if (hh.allergies)                   setAllergies(hh.allergies || "");
            if (hh.hormonal_status)             setHormonalStatus(hh.hormonal_status || "");
          } catch { /* non-fatal */ }
        }

        if (status.onboarding_complete) {
          setStep("success");
        } else if (status.profile_complete) {
          // Lifestyle / Health History / Complete steps are hidden — auto-complete after profile
          try { await api.post("/patient/onboarding/complete"); } catch { /* non-fatal */ }
          setStep("success");
        } else {
          setStep(1);
        }
      } catch {
        // No patient record yet — start from step 1
        setStep(1);
      }
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Profile photo upload ── */
  const uploadPhotoFile = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/patient/onboarding/upload-photo", form);
    setProfilePhoto(data.profile_photo_url);
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Photo must be under 5 MB."); return; }
    setProfilePhotoPreview(URL.createObjectURL(file));
    if (!isProfileSaved) {
      pendingPhotoRef.current = file;
      return;
    }
    setPhotoUploading(true); setError("");
    try {
      await uploadPhotoFile(file);
    } catch (err) {
      handleError(err);
      setProfilePhotoPreview("");
    } finally {
      setPhotoUploading(false);
    }
  };

  /* ── Step 1 submit — Profile ── */
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (displayName.trim().length < 2) return;
    setError(""); setLoading(true);
    const payload = {
      display_name: displayName.trim(),
      phone: phone.trim() || null,
      gender: gender || null,
      date_of_birth: dob || null,
      city: city.trim() || null,
      country: country.trim() || null,
      timezone: timezone || null,
    };
    try {
      if (isProfileSaved) {
        await api.put("/patient/onboarding/profile", payload);
      } else {
        await api.post("/patient/onboarding/profile", payload);
        setIsProfileSaved(true);
        if (pendingPhotoRef.current) {
          try {
            setPhotoUploading(true);
            await uploadPhotoFile(pendingPhotoRef.current);
            pendingPhotoRef.current = null;
          } catch { /* non-fatal */ } finally {
            setPhotoUploading(false);
          }
        }
      }
      // Lifestyle / Health History / Complete steps hidden — auto-complete after profile
      try { await api.post("/patient/onboarding/complete"); } catch { /* non-fatal */ }
      setStep("success");
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 2 submit — Lifestyle ── */
  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/patient/onboarding/lifestyle", {
        occupation: occupation.trim() || null,
        work_type: workType || null,
        diet_type: dietType || null,
        diet_notes: dietNotes.trim() || null,
        sleep_hours_avg: sleepHours !== "" ? Number(sleepHours) : null,
        sleep_quality: sleepQuality || null,
        exercise_frequency: exerciseFreq || null,
        exercise_types: exerciseTypes.length ? exerciseTypes : null,
        stress_level: stressLevel || null,
        alcohol_consumption: alcohol || null,
        smoking_status: smoking || null,
        water_intake_litres: waterIntake !== "" ? Number(waterIntake) : null,
      });
      setStep(3);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 3 submit — Health History ── */
  const handleStep3Submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/patient/onboarding/health-history", {
        existing_conditions: conditions.length ? conditions : null,
        past_surgeries: surgeries.trim() || null,
        family_history: familyHistory.length ? familyHistory : null,
        current_medications: medications.trim() || null,
        allergies: allergies.trim() || null,
        hormonal_status: hormonalStatus || null,
      });
      setStep(4);
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 4 submit — Complete ── */
  const handleComplete = async () => {
    setError(""); setLoading(true);
    try {
      await api.post("/patient/onboarding/complete");
      setStep("success");
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch { /* silent */ }
    onLogout?.();
  };

  const Bg = (
    <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: DOT_GRID, backgroundRepeat: "repeat", pointerEvents: "none", zIndex: 1 }} />
  );

  const OnboardingHeader = (
    <header style={{ position: "relative", zIndex: 10, flexShrink: 0, background: "#8AAEBB", borderBottom: "1px solid rgba(90,144,168,0.35)", padding: "0 28px", height: 58, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: 17, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.035em", fontFamily: "Inter, sans-serif" }}>
        Supercore Longevity
      </span>
      <button
        onClick={handleLogout}
        style={{ padding: "6px 16px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.45)", background: "transparent", color: "rgba(255,255,255,0.88)", fontSize: 13, fontFamily: "Inter, sans-serif", cursor: "pointer", transition: "all 150ms ease" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "#FFF"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.88)"; }}
      >
        Log out
      </button>
    </header>
  );

  /* ── Loading ── */
  if (step === null) {
    return (
      <div style={{ position: "relative", minHeight: "100vh", background: "#EDF4F7", display: "flex", flexDirection: "column" }}>
        {Bg}
        {OnboardingHeader}
        <div style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
          <div style={{ width: 40, height: 40, border: "3px solid rgba(90,144,168,0.2)", borderTopColor: "#5A90A8", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
          <p style={{ color: "#7AAABB", fontSize: 14, fontFamily: "Inter, sans-serif" }}>Loading your profile…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  /* ── Success screen ── */
  if (step === "success") {
    return (
      <div style={{ position: "relative", minHeight: "100vh", background: "#EDF4F7", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {Bg}
        {OnboardingHeader}
        <div style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px" }}>
          <div style={{ background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 22, padding: "48px 40px", width: "100%", maxWidth: 460, textAlign: "center", boxShadow: "0 4px 32px rgba(90,144,168,0.12)" }}>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(22,163,74,0.1)", border: "2px solid rgba(22,163,74,0.3)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            <h1 style={{ margin: "0 0 12px", fontSize: 22, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>You're all set!</h1>
            <p style={{ margin: "0 0 32px", fontSize: 14, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>
              Your profile, lifestyle data, and health history have been saved. Welcome to your longevity journey.
            </p>
            <button type="button" onClick={onFinish}
              style={{ padding: "13px 32px", background: "#5A90A8", color: "#FFFFFF", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: "pointer" }}>
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const stepTitle = {
    1: "Complete your profile",
    /* 2: "Your lifestyle & habits", 3: "Health history", 4: "Review & complete" — hidden */
  }[step];

  const stepSubtitle = {
    1: "Tell us about yourself to get started.",
    /* 2/3/4 hidden */
  }[step];

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: "#EDF4F7", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {Bg}
      {OnboardingHeader}

      <div style={{ position: "relative", zIndex: 2, flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", padding: "40px 24px 60px" }}>
      <div style={{ width: "100%", maxWidth: 600 }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 14px", borderRadius: 999, background: "rgba(90,144,168,0.1)", border: "1px solid rgba(90,144,168,0.3)", marginBottom: 16 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#5A90A8" }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: "#5A90A8", letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "Inter, sans-serif" }}>Patient Onboarding</span>
          </div>
          <h1 style={{ margin: "0 0 8px", fontSize: 26, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.025em" }}>{stepTitle}</h1>
          <p style={{ margin: 0, fontSize: 14, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>{stepSubtitle}</p>
        </div>

        {/* Stepper */}
        <Stepper current={step} />

        {/* Card */}
        <div style={{ background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.18)", borderRadius: 22, padding: "36px", boxShadow: "0 4px 32px rgba(90,144,168,0.12)" }}>

          {/* Error banner */}
          {error && (
            <div style={{ marginBottom: 24, padding: "11px 14px", borderRadius: 9, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif", lineHeight: 1.45 }}>
              {error}
            </div>
          )}

          {/* ════════════ STEP 1 — PROFILE ════════════ */}
          {step === 1 && (
            <form onSubmit={handleStep1Submit}>

              {/* Profile photo */}
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 28 }}>
                <label style={{ cursor: photoUploading ? "not-allowed" : "pointer", position: "relative" }}>
                  <input type="file" accept=".jpg,.jpeg,.png" onChange={handlePhotoSelect} disabled={photoUploading} style={{ display: "none" }} />
                  <div style={{ width: 88, height: 88, borderRadius: "50%", border: `2px dashed ${profilePhotoPreview ? "#5A90A8" : "rgba(90,144,168,0.3)"}`, background: profilePhotoPreview ? "transparent" : "rgba(90,144,168,0.04)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", transition: "border-color 180ms ease", position: "relative" }}>
                    {profilePhotoPreview
                      ? <img src={profilePhotoPreview} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(90,144,168,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>
                    }
                    <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "rgba(90,144,168,0.3)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 180ms ease" }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = "0"}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    </div>
                  </div>
                </label>
                <p style={{ margin: "8px 0 0", fontSize: 12, color: photoUploading ? "#5A90A8" : "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                  {photoUploading ? "Uploading…" : profilePhotoPreview ? "Click to change photo" : "Upload profile photo"}
                </p>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: "#A8C8D8", fontFamily: "Inter, sans-serif" }}>jpg, png · max 5 MB</p>
              </div>

              <p style={sectionLabel}>Identity</p>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Display name <span style={{ color: "#5A90A8" }}>*</span></label>
                <input type="text" placeholder="Jane Smith" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={fieldBase(focused.dn)} {...f("dn")} disabled={loading} />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Phone number<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", padding: "12px", background: "#F5FAFC", border: "1px solid rgba(90,144,168,0.25)", borderRadius: 10, color: "#4D7A8E", fontSize: 13, fontFamily: "Inter, sans-serif", whiteSpace: "nowrap", flexShrink: 0 }}>🇮🇳 +91</div>
                  <input type="tel" placeholder="9876543210" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} style={{ ...fieldBase(focused.ph), flex: 1 }} {...f("ph")} disabled={loading} />
                </div>
              </div>

              <div style={divider}><p style={sectionLabel}>Personal</p></div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Gender<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {GENDER_OPTIONS.map(({ label, value }) => (
                    <Pill key={value} label={label} active={gender === value} onClick={() => setGender(gender === value ? "" : value)} />
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Date of birth<span style={optionalTag}>optional</span></label>
                <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} max={new Date().toISOString().split("T")[0]} style={{ ...fieldBase(false), colorScheme: "light" }} disabled={loading} />
              </div>

              <div style={divider}><p style={sectionLabel}>Location</p></div>

              <div className="ob-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 18 }}>
                <div>
                  <label style={labelStyle}>City</label>
                  <input type="text" placeholder="Mumbai" value={city} onChange={(e) => setCity(e.target.value)} style={fieldBase(focused.city)} {...f("city")} disabled={loading} />
                </div>
                <div>
                  <label style={labelStyle}>Country</label>
                  <input type="text" placeholder="India" value={country} onChange={(e) => setCountry(e.target.value)} style={fieldBase(focused.country)} {...f("country")} disabled={loading} />
                </div>
              </div>

              <div style={{ marginBottom: 32 }}>
                <label style={labelStyle}>Timezone</label>
                <select value={timezone} onChange={(e) => setTimezone(e.target.value)} style={{ ...fieldBase(false), cursor: "pointer" }} disabled={loading}>
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>

              <SubmitBtn canSubmit={!loading && displayName.trim().length >= 2} loading={loading} label="Save & Continue →" />
            </form>
          )}

          {/* ════════════ STEP 2 — LIFESTYLE ════════════ */}
          {false && step === 2 && (
            <form onSubmit={handleStep2Submit}>
              <p style={sectionLabel}>Work</p>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Occupation<span style={optionalTag}>optional</span></label>
                <input type="text" placeholder="e.g. Software Engineer, Teacher…" value={occupation} onChange={(e) => setOccupation(e.target.value)} style={fieldBase(focused.occ)} {...f("occ")} disabled={loading} />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Work type<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {WORK_TYPES.map(({ label, value }) => (
                    <Pill key={value} label={label} active={workType === value} onClick={() => setWorkType(workType === value ? "" : value)} />
                  ))}
                </div>
              </div>

              <div style={divider}><p style={sectionLabel}>Nutrition</p></div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Diet type<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {DIET_TYPES.map(({ label, value }) => (
                    <Pill key={value} label={label} active={dietType === value} onClick={() => setDietType(dietType === value ? "" : value)} />
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Diet notes<span style={optionalTag}>optional</span></label>
                <textarea value={dietNotes} onChange={(e) => setDietNotes(e.target.value)} placeholder="Any dietary restrictions, preferences, or context…" style={textareaBase(focused.dn2)} {...f("dn2")} disabled={loading} />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Daily water intake (litres)<span style={optionalTag}>optional</span></label>
                <input type="number" min={0} max={20} step={0.1} placeholder="e.g. 2.5" value={waterIntake} onChange={(e) => setWaterIntake(e.target.value)} style={fieldBase(focused.water)} {...f("water")} disabled={loading} />
              </div>

              <div style={divider}><p style={sectionLabel}>Sleep & Recovery</p></div>

              <div className="ob-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>
                <div>
                  <label style={labelStyle}>Sleep hours (avg)<span style={optionalTag}>0–12</span></label>
                  <input type="number" min={0} max={12} step={0.5} placeholder="e.g. 7.5" value={sleepHours} onChange={(e) => setSleepHours(e.target.value)} style={fieldBase(focused.sl)} {...f("sl")} disabled={loading} />
                </div>
                <div>
                  <label style={labelStyle}>Sleep quality<span style={optionalTag}>optional</span></label>
                  <select value={sleepQuality} onChange={(e) => setSleepQuality(e.target.value)} style={{ ...fieldBase(false), cursor: "pointer" }} disabled={loading}>
                    <option value="">Select…</option>
                    {SLEEP_QUALITY.map(({ label, value }) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
              </div>

              <div style={divider}><p style={sectionLabel}>Exercise</p></div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Exercise frequency<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {EXERCISE_FREQUENCY.map(({ label, value }) => (
                    <Pill key={value} label={label} active={exerciseFreq === value} onClick={() => setExerciseFreq(exerciseFreq === value ? "" : value)} />
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ ...labelStyle, marginBottom: 10 }}>Exercise types<span style={optionalTag}>type and press Enter</span></label>
                <TagInput tags={exerciseTypes} onChange={setExerciseTypes} placeholder="e.g. Running, Yoga…" suggestions={EXERCISE_SUGGESTIONS} disabled={loading} />
              </div>

              <div style={divider}><p style={sectionLabel}>Lifestyle Factors</p></div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Stress level<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {STRESS_LEVELS.map(({ label, value }) => (
                    <Pill key={value} label={label} active={stressLevel === value} onClick={() => setStressLevel(stressLevel === value ? "" : value)} />
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Alcohol consumption<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {ALCOHOL_OPTIONS.map(({ label, value }) => (
                    <Pill key={value} label={label} active={alcohol === value} onClick={() => setAlcohol(alcohol === value ? "" : value)} />
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 32 }}>
                <label style={labelStyle}>Smoking status<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {SMOKING_OPTIONS.map(({ label, value }) => (
                    <Pill key={value} label={label} active={smoking === value} onClick={() => setSmoking(smoking === value ? "" : value)} />
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <button type="button" onClick={() => { setError(""); setStep(1); }}
                  style={{ flex: "0 0 auto", padding: "14px 20px", background: "rgba(90,144,168,0.06)", color: "#7AAABB", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#5A90A8"; e.currentTarget.style.color = "#1C3545"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.2)"; e.currentTarget.style.color = "#7AAABB"; }}
                >← Back</button>
                <div style={{ flex: 1 }}>
                  <SubmitBtn canSubmit={!loading} loading={loading} label="Save & Continue →" />
                </div>
              </div>
            </form>
          )}

          {/* ════════════ STEP 3 — HEALTH HISTORY ════════════ */}
          {false && step === 3 && (
            <form onSubmit={handleStep3Submit}>
              <p style={sectionLabel}>Medical Conditions</p>

              <div style={{ marginBottom: 18 }}>
                <label style={{ ...labelStyle, marginBottom: 10 }}>Existing conditions<span style={optionalTag}>type and press Enter</span></label>
                <TagInput tags={conditions} onChange={setConditions} placeholder="e.g. Diabetes, Hypertension…" suggestions={CONDITION_SUGGESTIONS} disabled={loading} />
              </div>

              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Past surgeries<span style={optionalTag}>optional</span></label>
                <textarea value={surgeries} onChange={(e) => setSurgeries(e.target.value)} placeholder="List any surgeries, procedures, or hospitalisations…" style={textareaBase(focused.surg)} {...f("surg")} disabled={loading} />
              </div>

              <div style={divider}><p style={sectionLabel}>Family History</p></div>

              <div style={{ marginBottom: 24 }}>
                <label style={{ ...labelStyle, marginBottom: 10 }}>Family health history<span style={optionalTag}>type and press Enter</span></label>
                <TagInput tags={familyHistory} onChange={setFamilyHistory} placeholder="e.g. Diabetes, Heart Disease…" suggestions={["Diabetes", "Heart Disease", "Cancer", "Hypertension", "Alzheimer's", "Stroke"]} disabled={loading} />
              </div>

              <div style={divider}><p style={sectionLabel}>Current Health</p></div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Current medications<span style={optionalTag}>optional — include dosage if known</span></label>
                <textarea value={medications} onChange={(e) => setMedications(e.target.value)} placeholder="List all medications, supplements, vitamins…" style={textareaBase(focused.meds)} {...f("meds")} disabled={loading} />
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={labelStyle}>Allergies<span style={optionalTag}>optional</span></label>
                <textarea value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="Drug, food, or environmental allergies…" style={textareaBase(focused.allg)} {...f("allg")} disabled={loading} />
              </div>

              <div style={{ marginBottom: 32 }}>
                <label style={labelStyle}>Hormonal status<span style={optionalTag}>optional</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {HORMONAL_STATUS.map(({ label, value }) => (
                    <Pill key={value} label={label} active={hormonalStatus === value} onClick={() => setHormonalStatus(hormonalStatus === value ? "" : value)} />
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <button type="button" onClick={() => { setError(""); setStep(2); }}
                  style={{ flex: "0 0 auto", padding: "14px 20px", background: "rgba(90,144,168,0.06)", color: "#7AAABB", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#5A90A8"; e.currentTarget.style.color = "#1C3545"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.2)"; e.currentTarget.style.color = "#7AAABB"; }}
                >← Back</button>
                <div style={{ flex: 1 }}>
                  <SubmitBtn canSubmit={!loading} loading={loading} label="Save & Continue →" />
                </div>
              </div>
            </form>
          )}

          {/* ════════════ STEP 4 — REVIEW & COMPLETE ════════════ */}
          {false && step === 4 && (
            <div>
              <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 32 }}>
                {[
                  { label: "Basic Profile",   done: true, icon: "👤" },
                  { label: "Lifestyle & Habits", done: true, icon: "🏃" },
                  { label: "Health History",  done: true, icon: "🩺" },
                ].map(({ label, done, icon }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", borderRadius: 14, border: "1px solid rgba(22,163,74,0.2)", background: "rgba(22,163,74,0.04)" }}>
                    <span style={{ fontSize: 20 }}>{icon}</span>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: "#1C3545", fontFamily: "Inter, sans-serif" }}>{label}</span>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(22,163,74,0.15)", border: "2px solid rgba(22,163,74,0.4)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#16A34A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ padding: "16px 18px", borderRadius: 12, background: "rgba(90,144,168,0.06)", border: "1px solid rgba(90,144,168,0.18)", marginBottom: 28 }}>
                <p style={{ margin: 0, fontSize: 13, color: "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.6 }}>
                  Your information is encrypted and only shared with your care team. You can update any section at any time from your dashboard.
                </p>
              </div>

              <div style={{ display: "flex", gap: 12 }}>
                <button type="button" onClick={() => { setError(""); setStep(3); }}
                  style={{ flex: "0 0 auto", padding: "14px 20px", background: "rgba(90,144,168,0.06)", color: "#7AAABB", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#5A90A8"; e.currentTarget.style.color = "#1C3545"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.2)"; e.currentTarget.style.color = "#7AAABB"; }}
                >← Back</button>
                <button type="button" onClick={handleComplete} disabled={loading}
                  style={{ flex: 1, padding: "14px", background: loading ? "rgba(90,144,168,0.25)" : "#5A90A8", color: loading ? "rgba(90,144,168,0.5)" : "#FFFFFF", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: "Inter, sans-serif", cursor: loading ? "not-allowed" : "pointer", transition: "background 180ms ease, transform 180ms ease, box-shadow 180ms ease" }}
                  onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = "#4D7A8E"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(90,144,168,0.3)"; } }}
                  onMouseLeave={(e) => { if (!loading) { e.currentTarget.style.background = "#5A90A8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}
                >
                  {loading ? "Completing…" : "Complete Setup ✓"}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
      </div>
    </div>
  );
}
